<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useId, watch } from 'vue';
import { computePopoverPosition, type PopoverAnchorRect } from './popoverPosition';
import LumiIcon from '../icons/LumiIcon.vue';

/**
 * A small action menu: one trigger, a list of items, keyboard navigation and
 * focus return. The reusable shape of the ad-hoc "three dots" popovers the apps
 * kept hand-rolling (backoffice-ui's JobCard move popover was the precedent).
 *
 * Copy comes in through `items[].label` and the required `label` (the trigger's
 * accessible name — an icon-only trigger carries none of its own, same rule as
 * IconButton); the host app translates. An item with `href` renders as a link
 * and still closes the menu on activation; everything else is a button that
 * emits `select` with the item's key.
 *
 * Not modal: Tab moves on and the menu closes once focus has actually left it
 * (`focusout`), a click outside closes it, and Escape closes it and hands focus
 * back to the trigger. Arrow keys, Home and End move between the enabled items
 * and wrap. Tab is deliberately NOT handled on keydown: unmounting the panel at
 * that moment, while the browser is still choosing the next tab stop, drops
 * focus on `body` instead of the next control.
 *
 * Two ways to open it without the trigger button: `anchor` (a viewport point, a
 * rect or an element) places the panel there and drops the trigger — a context
 * menu opened by a right-click, or a menu opened from another component's own
 * button — and `open` (`v-model:open`) lets the host open and close it. Both
 * work with the trigger too.
 */
export interface MenuItem {
  key: string;
  label: string;
  /** Renders the item in the danger tone — for the one irreversible action in the list. */
  danger?: boolean;
  disabled?: boolean;
  /** Render the item as a link instead of a button. */
  href?: string;
  target?: string;
  rel?: string;
  /** Optional stable test hook for the rendered item, since `key` alone is not one. */
  testId?: string;
  /**
   * A quiet line of explanation under the item (typically WHY it is disabled).
   * Not interactive; the item's button points at it through `aria-describedby`.
   */
  hint?: string;
  /** Draws a hairline above the item, parting it from the group before it. */
  separated?: boolean;
  /**
   * Makes the item a `menuitemradio` that is (or is not) the current choice,
   * with a check glyph when it is. Leave it undefined for a plain action.
   */
  checked?: boolean;
  /**
   * Activating the item emits `select` but leaves the menu open — for an item
   * that swaps `items` in place (a "Connect to…" sub-list). Focus moves to the
   * first enabled item of the new list.
   */
  keepOpen?: boolean;
}

/** Where an anchored menu opens: a viewport point (a pointer), a rect, or an element. */
export type MenuAnchor = { x: number; y: number } | PopoverAnchorRect | HTMLElement;

const props = withDefaults(
  defineProps<{
    items: MenuItem[];
    /** Accessible name of the trigger (aria-label + native tooltip). */
    label: string;
    /** Which edge of the trigger the panel aligns to. */
    align?: 'start' | 'end';
    size?: 'sm' | 'md';
    /**
     * `ghost` (the default) is the quiet dots in a row or a card; `secondary` is the
     * bordered white square a page toolbar puts at its end, beside IconButton's own
     * secondary squares, so the menu reads as a control rather than as decoration.
     */
    variant?: 'ghost' | 'secondary';
    disabled?: boolean;
    /**
     * Teleports the panel to `body`. For a menu in a sticky bar (a toolbar is a
     * stacking context): in place, the panel is capped at the bar's layer and
     * opens behind the page's own sticky headers. Not for a menu inside a
     * Modal/Drawer — a body-level panel at the popover layer sits under the
     * overlay. The panel is already `position: fixed`, so nothing else changes.
     */
    teleport?: boolean;
    /** Test hook for the trigger button (attrs land on the wrapper, not the button). */
    triggerTestId?: string;
    /**
     * Opens the panel at a point, a rect or an element instead of at the trigger,
     * and renders no trigger button (a context menu; a menu opened from a button
     * that belongs to another component). The host then controls `open`.
     */
    anchor?: MenuAnchor;
    /** Controlled open state (`v-model:open`); leave undefined to let the trigger drive it. */
    open?: boolean;
    /** A trigger that fills the width it is given and sizes to its own content (a switcher). */
    block?: boolean;
  }>(),
  { align: 'end', size: 'md', variant: 'ghost', disabled: false, teleport: false, triggerTestId: undefined, anchor: undefined, open: undefined, block: false },
);

const emit = defineEmits<{ select: [key: string]; open: []; close: []; 'update:open': [open: boolean] }>();

const isOpen = ref(false);
let restoreFocusTo: HTMLElement | null = null;
let focusFirstOnItemsChange = false;
const rootRef = ref<HTMLElement | null>(null);
const triggerRef = ref<HTMLButtonElement | null>(null);
const panelRef = ref<HTMLElement | null>(null);
const itemRefs = ref<HTMLElement[]>([]);
const menuId = useId();

const enabledIndexes = computed(() =>
  props.items.map((item, index) => (item.disabled ? -1 : index)).filter((index) => index >= 0),
);

function setItemRef(el: unknown, index: number) {
  if (el) itemRefs.value[index] = el as HTMLElement;
}

function focusItem(index: number) {
  const el = itemRefs.value[index];
  // preventScroll: the panel is fixed to the viewport (see placePanel), so a
  // focus-driven scroll of the ancestor it lives in could never reveal it — it
  // would only fire the scroll that closes the menu.
  if (el) el.focus({ preventScroll: true });
}

// A menu with nothing enabled (one disabled item and the reason it is disabled)
// has no item that can take focus -- a disabled button is unfocusable. Focus the
// panel itself instead: it is described by the hints (below), so a screen reader
// reads the reason, and Escape (handled on the panel) still closes it.
function focusPanel() {
  panelRef.value?.focus({ preventScroll: true });
}

function focusFirst() {
  const first = enabledIndexes.value[0];
  if (first !== undefined) focusItem(first);
  else focusPanel();
}

function focusLast() {
  const last = enabledIndexes.value[enabledIndexes.value.length - 1];
  if (last !== undefined) focusItem(last);
  else focusPanel();
}

/** The hint ids the panel is described by -- only while no item can take focus. */
const panelDescribedBy = computed(() => {
  if (enabledIndexes.value.length > 0) return undefined;
  const ids = props.items.filter((item) => item.hint).map((item) => `${menuId}-${item.key}-hint`);
  return ids.length > 0 ? ids.join(' ') : undefined;
});

function currentIndex(): number {
  const active = typeof document === 'undefined' ? null : document.activeElement;
  return itemRefs.value.findIndex((el) => el === active);
}

function focusRelative(step: 1 | -1) {
  const enabled = enabledIndexes.value;
  if (enabled.length === 0) return;
  const position = enabled.indexOf(currentIndex());
  const next = position === -1 ? (step === 1 ? 0 : enabled.length - 1) : (position + step + enabled.length) % enabled.length;
  focusItem(enabled[next]);
}

// The panel is `position: fixed`, placed from the trigger's viewport rect, so no
// ancestor that clips or scrolls — a table frame scrolling its own rows (#471), a
// card with overflow hidden — can cut it off. It opens below the trigger and
// flips above when the viewport has no room below. It does not chase a scrolling
// ancestor: any scroll outside the panel, or a resize, closes it instead.
const PANEL_GAP = 4;
const VIEWPORT_EDGE = 8;

function anchorRect(): PopoverAnchorRect | null {
  const a = props.anchor;
  if (!a) return triggerRef.value?.getBoundingClientRect() ?? null;
  if (typeof HTMLElement !== 'undefined' && a instanceof HTMLElement) return a.getBoundingClientRect();
  if ('top' in a) return a as PopoverAnchorRect;
  const point = a as { x: number; y: number };
  return { top: point.y, bottom: point.y, left: point.x, right: point.x };
}

function placePanel() {
  const rect = anchorRect();
  const panel = panelRef.value;
  if (!rect || !panel || typeof window === 'undefined') return;
  // Where (0,0) actually lands: a transformed ancestor becomes the containing
  // block of a fixed element, so offsets are corrected against it, not assumed.
  panel.style.top = '0px';
  panel.style.left = '0px';
  const origin = panel.getBoundingClientRect();
  const { top, left } = computePopoverPosition(
    rect,
    { width: origin.width, height: origin.height },
    { width: window.innerWidth, height: window.innerHeight },
    { align: props.align, gap: PANEL_GAP, edge: VIEWPORT_EDGE },
  );
  panel.style.top = `${top - origin.top}px`;
  panel.style.left = `${left - origin.left}px`;
}

async function openMenu(focus: 'first' | 'last' | 'none' = 'first') {
  if (props.disabled || isOpen.value) return;
  if (typeof document !== 'undefined') {
    const active = document.activeElement;
    restoreFocusTo = props.anchor instanceof HTMLElement ? props.anchor : active instanceof HTMLElement ? active : null;
  }
  isOpen.value = true;
  emit('open');
  emit('update:open', true);
  await nextTick();
  placePanel();
  if (focus === 'first') focusFirst();
  else if (focus === 'last') focusLast();
}

function closeMenu(returnFocus = true) {
  if (!isOpen.value) return;
  isOpen.value = false;
  emit('close');
  emit('update:open', false);
  if (returnFocus) (triggerRef.value ?? restoreFocusTo)?.focus();
  restoreFocusTo = null;
}

function toggle() {
  if (isOpen.value) closeMenu();
  else void openMenu('first');
}

function onTriggerKeydown(e: KeyboardEvent) {
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    void openMenu('first');
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    void openMenu('last');
  }
}

function onMenuKeydown(e: KeyboardEvent) {
  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault();
      focusRelative(1);
      break;
    case 'ArrowUp':
      e.preventDefault();
      focusRelative(-1);
      break;
    case 'Home':
      e.preventDefault();
      focusFirst();
      break;
    case 'End':
      e.preventDefault();
      focusLast();
      break;
    case 'Escape':
      e.preventDefault();
      e.stopPropagation();
      closeMenu();
      break;
    default:
      break;
  }
}

function activate(item: MenuItem) {
  if (item.disabled) return;
  emit('select', item.key);
  if (item.keepOpen) focusFirstOnItemsChange = true;
  else closeMenu();
}

// A keepOpen item swapped `items` in place: the panel changed size, so it is
// placed again from the same anchor, and focus starts on the new list.
watch(
  () => props.items,
  async () => {
    if (!focusFirstOnItemsChange) return;
    focusFirstOnItemsChange = false;
    itemRefs.value = [];
    await nextTick();
    placePanel();
    focusFirst();
  },
);

// The host opens and closes it (controlled `open`); an anchor that moves while
// open (a second right-click) moves the panel with it.
watch(
  () => props.open,
  (want) => {
    if (want === undefined) return;
    if (want) void openMenu('first');
    else closeMenu(false);
  },
);
watch(
  () => props.anchor,
  async () => {
    if (!isOpen.value) return;
    await nextTick();
    placePanel();
  },
);
onMounted(() => {
  if (props.open) void openMenu('first');
});

// A teleported panel is not a DOM descendant of the root: it counts as inside.
function isInside(node: Node): boolean {
  const anchorEl = props.anchor instanceof HTMLElement ? props.anchor : null;
  return !!rootRef.value?.contains(node) || !!panelRef.value?.contains(node) || !!anchorEl?.contains(node);
}

function onDocumentPointerDown(e: Event) {
  const target = e.target as Node | null;
  if (rootRef.value && target && !isInside(target)) closeMenu(false);
}

function onFocusOut(e: FocusEvent) {
  const next = e.relatedTarget as Node | null;
  if (next && rootRef.value && !isInside(next)) closeMenu(false);
}

function onViewportChange(e: Event) {
  const target = e.target;
  if (e.type === 'scroll' && panelRef.value && target instanceof Node && panelRef.value.contains(target)) return;
  closeMenu(false);
}

function unlisten() {
  document.removeEventListener('pointerdown', onDocumentPointerDown, true);
  document.removeEventListener('scroll', onViewportChange, true);
  window.removeEventListener('resize', onViewportChange);
}

watch(isOpen, (nowOpen) => {
  if (typeof document === 'undefined') return;
  if (nowOpen) {
    document.addEventListener('pointerdown', onDocumentPointerDown, true);
    document.addEventListener('scroll', onViewportChange, true);
    window.addEventListener('resize', onViewportChange);
  } else {
    unlisten();
  }
});

onBeforeUnmount(() => {
  if (typeof document === 'undefined') return;
  unlisten();
});

defineExpose({ open: openMenu, close: closeMenu });
</script>

<template>
  <div
    ref="rootRef"
    class="lumi-menu"
    :class="{ 'lumi-menu--anchored': anchor, 'lumi-menu--block': block }"
    @focusout="onFocusOut"
  >
    <button
      v-if="!anchor"
      ref="triggerRef"
      type="button"
      class="lumi-menu__trigger"
      :class="[
        block ? 'lumi-menu__trigger--block' : `lumi-menu__trigger--${size}`,
        `lumi-menu__trigger--${variant}`,
        { 'lumi-menu__trigger--open': isOpen },
      ]"
      :aria-label="label"
      :title="label"
      :data-testid="triggerTestId"
      aria-haspopup="menu"
      :aria-expanded="isOpen ? 'true' : 'false'"
      :disabled="disabled"
      @click="toggle"
      @keydown="onTriggerKeydown"
    >
      <slot>
        <LumiIcon v-if="variant === 'secondary'" name="more" width="20" height="20" stroke-width="2" />
        <LumiIcon v-else name="more" width="18" height="18" />
      </slot>
    </button>
    <Teleport to="body" :disabled="!teleport">
    <div
      v-if="isOpen"
      ref="panelRef"
      class="lumi-menu__panel"
      :class="{ 'lumi-menu__panel--start': align === 'start' }"
      role="menu"
      :aria-label="label"
      :tabindex="enabledIndexes.length === 0 ? -1 : undefined"
      :aria-describedby="panelDescribedBy"
      @keydown="onMenuKeydown"
      @focusout="onFocusOut"
    >
      <template v-for="(item, index) in items" :key="item.key">
        <div v-if="item.separated && index > 0" class="lumi-menu__sep" role="separator"></div>
        <a
          v-if="item.href && !item.disabled"
          :ref="(el) => setItemRef(el, index)"
          class="lumi-menu__item"
          :class="{ 'lumi-menu__item--danger': item.danger }"
          role="menuitem"
          tabindex="-1"
          :href="item.href"
          :target="item.target"
          :rel="item.rel"
          :data-testid="item.testId"
          @click="activate(item)"
        >
          {{ item.label }}
        </a>
        <button
          v-else
          :ref="(el) => setItemRef(el, index)"
          type="button"
          class="lumi-menu__item"
          :class="{ 'lumi-menu__item--danger': item.danger, 'lumi-menu__item--check': item.checked !== undefined }"
          :role="item.checked !== undefined ? 'menuitemradio' : 'menuitem'"
          :aria-checked="item.checked !== undefined ? (item.checked ? 'true' : 'false') : undefined"
          tabindex="-1"
          :disabled="item.disabled"
          :aria-disabled="item.disabled ? 'true' : undefined"
          :aria-describedby="item.hint ? `${menuId}-${item.key}-hint` : undefined"
          :data-testid="item.testId"
          @click="activate(item)"
        >
          {{ item.label }}
          <LumiIcon v-if="item.checked" name="confirm" class="lumi-menu__check" width="16" height="16" stroke-width="2.5" />
        </button>
        <p v-if="item.hint" :id="`${menuId}-${item.key}-hint`" class="lumi-menu__hint">{{ item.hint }}</p>
      </template>
    </div>
    </Teleport>
  </div>
</template>

<style>
.lumi-menu{position:relative;display:inline-block;font-family:var(--font-body);}
.lumi-menu--block{display:block;}
/* An anchored menu has no trigger: its root is only the parent of the fixed panel. */
.lumi-menu--anchored{display:contents;}
.lumi-menu__trigger{display:inline-flex;align-items:center;justify-content:center;border-radius:var(--radius-md);border:1px solid transparent;background:transparent;color:var(--text-secondary);cursor:pointer;padding:0;transition:background-color var(--dur-fast) var(--ease-standard),color var(--dur-fast) var(--ease-standard);}
.lumi-menu__trigger--sm{width:var(--control-h-sm);height:var(--control-h-sm);}
.lumi-menu__trigger--md{width:var(--control-h);height:var(--control-h);}
.lumi-menu__trigger--block{display:flex;width:100%;height:auto;justify-content:flex-start;text-align:left;color:inherit;font:inherit;}
.lumi-menu__trigger:hover:not(:disabled),.lumi-menu__trigger--open{background:var(--surface-wash);color:var(--text-heading);}
.lumi-menu__trigger--secondary{background:var(--surface-card);border-color:var(--border-strong);color:var(--text-heading);box-shadow:var(--shadow-sm);}
.lumi-menu__trigger:disabled{opacity:.5;cursor:not-allowed;}
/* The popover step of the layer ladder (tokens/layers.css). The panel wears the
   dialog style in miniature: surface, panel edge, floating shadow. Fixed, not
   absolute: placePanel() sets top/left from the trigger, so a clipping or
   scrolling ancestor (a Table frame, #471) cannot cut it off. */
.lumi-menu__panel{position:fixed;top:0;left:0;min-width:200px;padding:var(--space-1);background:var(--surface-card);border:1px solid var(--panel-edge);border-radius:var(--radius-lg);box-shadow:var(--shadow-lg);z-index:var(--layer-popover);display:flex;flex-direction:column;max-height:min(24rem,calc(100vh - 16px));overflow-y:auto;}
.lumi-menu__panel:focus{outline:none;}
.lumi-menu__item{display:block;width:100%;text-align:left;padding:var(--space-2) var(--space-3);border:none;border-radius:var(--radius-sm);background:transparent;color:var(--text-body);font:inherit;font-size:var(--text-sm);line-height:1.4;cursor:pointer;text-decoration:none;white-space:nowrap;}
.lumi-menu__item:hover:not(:disabled),.lumi-menu__item:focus-visible{background:var(--surface-wash);color:var(--text-heading);outline:none;}
.lumi-menu__item:disabled{opacity:.5;cursor:not-allowed;}
.lumi-menu__item--check{display:flex;align-items:center;justify-content:space-between;gap:var(--space-3);}
.lumi-menu__check{flex:none;color:var(--accent);}
.lumi-menu__hint{margin:0;padding:0 var(--space-3) var(--space-2);font-size:var(--text-xs);line-height:1.4;color:var(--text-tertiary);white-space:normal;max-width:260px;}
.lumi-menu__sep{height:1px;margin:var(--space-1) var(--space-2);background:var(--border-default);}
.lumi-menu__item--danger{color:var(--danger-text);}
.lumi-menu__item--danger:hover:not(:disabled),.lumi-menu__item--danger:focus-visible{background:var(--danger-soft);color:var(--danger-text);}
</style>
